import { useEffect, useMemo, useState } from 'react';
import { GridCellProps } from '@progress/kendo-react-grid';
import { Button, Box, Chip, Tooltip } from '@mui/material';
import moment from 'moment';
import { Activity } from 'src/types/activity';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import crmApi from 'src/api/CrmApi';
import { CRMActivityEntity } from 'src/types/CRMActivityEntity';
import useCrm from 'src/hooks/useCrm';
import useUpdateEffect from 'src/hooks/useUpdateEffect';
import AttachFileIcon from '@mui/icons-material/AttachFile';

interface Props extends GridCellProps {
  dataItem: any;
  onEdit: (e: any) => void;
  onDelete: (e: any) => void;
  searchText: string;
  expandAllActivities: boolean;
}

const ActivityCell = (props: Props) => {
  const dataItem = props.dataItem as Activity;
  const searchText = (props.searchText ?? '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const { attachmentClicked, reloadCellWithThisActivityId, setReloadCellWithThisActivityId } = useCrm();
  const [activity, setActivity] = useState<CRMActivityEntity>(null);
  const [detailsExpanded, setDetailsExpanded] = useState<boolean>(props.expandAllActivities);
  const regex = /([a-zA-Z0-9._-]+)@([a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/g;

  const titleParts = useMemo(() => {
    // If this activity was created at the end of the OrderSubmit flow, append the PO# and Order# to the title
    const title = dataItem.orderNumber ? dataItem.title + ` | PO#: ${dataItem.poNumber}, ORDER#: ${dataItem.orderNumber}-${dataItem.orderSuffix}` : dataItem.title;

    return searchText.trim() !== '' ? title.split(new RegExp(`(${searchText})`, 'gi')) : [title];
  }, [dataItem.title, searchText]);

  const contentParts = useMemo(() => {
    return searchText.trim() !== '' ? dataItem.content.split(new RegExp(`(${searchText})`, 'gi')) : [dataItem.content];
  }, [dataItem.content, searchText]);

  const highlightedContent = useMemo(() => {
    return contentParts.reduce((accumulator, part, i) => {
      if (part && part.toLowerCase() === searchText.toLowerCase()) {
        return accumulator + `<span key=${i} style="background-color: yellow">${part}</span>`;
      } else {
        return accumulator + part;
      }
    }, '');
  }, [contentParts, searchText]);

  const expandedState = useMemo(() => {
    return detailsExpanded || contentParts.length > 1;
  }, [detailsExpanded, contentParts]);

  function loadActivity() {
    crmApi.getCrmActivity(dataItem.activityId).then((crmActivity) => {
      setActivity(crmActivity);
    });
  }

  useEffect(() => {
    if (expandedState) {
      // Note: Error handling should be introduced here
      loadActivity();
    }
  }, [expandedState]);

  useUpdateEffect(() => {
    if (reloadCellWithThisActivityId === dataItem.activityId && expandedState) {
      loadActivity();
      setReloadCellWithThisActivityId(null);
    }
  }, [reloadCellWithThisActivityId]);

  //Get attachments that are not marked as deleted
  const filteredAttachments = useMemo(() => {
    return activity?.attachments?.filter((item: any) => !item.isDeleted) || [];
  }, [activity]);

  return (
    <td className="activity-grid__activity-cell" data-grid-col-index={dataItem.activityId}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div>
          {dataItem?.dueDate && (
            <div className="activity-grid__activity-cell-due-date d-flex d-md-none">
              <CalendarTodayIcon /> {moment(dataItem?.dueDate).format('MMMM D YYYY')}
            </div>
          )}
          <div className="activity-grid__activity-cell-title">
            <Button variant="text" disableRipple onClick={() => setDetailsExpanded((prev) => !prev)}>
              {expandedState ? <KeyboardArrowDownIcon /> : <ChevronRightIcon />}
              {titleParts.map((part, i) =>
                part.toLowerCase() === searchText.toLowerCase() ? (
                  <span key={i} style={{ backgroundColor: 'yellow' }}>
                    {part}
                  </span>
                ) : (
                  part
                )
              )}

              {dataItem.totalAttachments > 0 && (
                <Tooltip title={`${dataItem?.totalAttachments} attachments`}>
                  <AttachFileIcon fontSize="small" className="activity-grid__icon__attachment" />
                </Tooltip>
              )}
            </Button>
          </div>

          {expandedState ? (
            <div className="activity-grid__activity-cell-details">
              <div className="activity-grid__activity-cell-customer d-block d-md-none">{dataItem.customerName}</div>

              {dataItem.content && (
                <Box sx={{ pb: 1 }}>
                  <b>Content:</b>

                  <div dangerouslySetInnerHTML={{ __html: highlightedContent }} />
                </Box>
              )}

              {activity?.programs.length ? (
                <Box sx={{ pb: 1 }}>
                  <b>Programs Discussed:</b> {activity.programs.map((program) => program.programName).join(', ')}
                </Box>
              ) : null}

              {activity?.contacts.length ? (
                <Box sx={{ pb: 1 }}>
                  <b>Attendees:</b> {activity.contacts.map((c) => c.contactName).join(', ')}
                </Box>
              ) : null}

              {dataItem.activityType === 'Call' && (
                <Box sx={{ pb: 1 }}>
                  <b>Call Direction:</b> {dataItem.callDirection}
                </Box>
              )}

              {(dataItem.activityType === 'Call' || dataItem.activityType === 'Communication') && (
                <Box sx={{ pb: 1 }}>
                  <b>Has Responded:</b> {dataItem.didRespond ? 'Yes' : 'No'}
                </Box>
              )}

              {dataItem?.assignedToUsername != null && dataItem?.assignedToUsername !== '' && (
                <Box sx={{ pb: 1 }}>
                  <b>Assigned to:</b> {dataItem?.assignedToUsername.replace(regex, '$1')}
                </Box>
              )}

              {filteredAttachments.length ? (
                <Box sx={{ pb: 1 }}>
                  <b>Attachments:</b>{' '}
                  {filteredAttachments.map((item, i) => (
                    <Chip label={item.name} key={i} sx={{ mr: 1, padding: 1 }} onClick={() => attachmentClicked(item)} />
                  ))}
                </Box>
              ) : null}

              <Box sx={{ pt: 1 }}>
                <Button size="small" className="btn-util" variant="contained" onClick={() => props.onEdit(dataItem)}>
                  Edit
                </Button>
                <Button size="small" className="btn-util" variant="contained" onClick={() => props.onDelete(dataItem)}>
                  Delete
                </Button>
              </Box>
            </div>
          ) : null}

          <div className="activity-grid__activity-cell-subtitle">
            #{dataItem?.activityId} Created by {dataItem?.createdByUsername ? dataItem.createdByUsername.replace(regex, '$1') : ''}
          </div>
        </div>
        {dataItem?.dueDate && (
          <div className="d-none ">
            <div className="activity-grid__activity-cell-due-date d-md-flex">
              <CalendarTodayIcon />
              Due: {moment(dataItem?.dueDate).format('MMMM D YYYY')}
            </div>
          </div>
        )}
        {dataItem?.followUpDate && (
          <div className="d-none d-md-flex">
            <div className="activity-grid__activity-cell-due-date d-md-flex">
              <CalendarTodayIcon />
              Follow up: {moment(dataItem?.followUpDate).format('MMMM D YYYY')}
            </div>
          </div>
        )}
      </div>
    </td>
  );
};

export default ActivityCell;
